import { isAdminRole } from "../auth/roles";

export const AGENT_FIX_STATUSES = [
  "queued",
  "working",
  "pr_open",
  "needs_person",
] as const;

export type AgentFixStatus = (typeof AGENT_FIX_STATUSES)[number];

export const AGENT_FIX_STATUS_LABEL: Record<AgentFixStatus, string> = {
  queued: "Queued",
  working: "Agent working",
  pr_open: "PR open",
  needs_person: "Needs a person",
};

export const AGENT_FIX_REPORTED_STATUSES = ["working", "needs_person"] as const;

export type AgentFixReportedStatus = (typeof AGENT_FIX_REPORTED_STATUSES)[number];

export const AGENT_FIX_STARTERS = ["staff", "agent_key", "system"] as const;

export type AgentFixStarterKind = (typeof AGENT_FIX_STARTERS)[number];

export const AGENT_FIX_STARTER_LABEL: Record<AgentFixStarterKind, string> = {
  staff: "Requested by staff",
  agent_key: "Requested by an agent",
  system: "Started automatically",
};

export const AGENT_FIX_EVENTS = ["created", "released", "reopened", "unblocked", "retry", "manual"] as const;

export type AgentFixEvent = (typeof AGENT_FIX_EVENTS)[number];

export const AGENT_FIX_TRUST_LEVELS = ["staff", "untrusted"] as const;

export type AgentFixTrust = (typeof AGENT_FIX_TRUST_LEVELS)[number];

const OUTSIDE_TEXT_SOURCES: ReadonlySet<string> = new Set(["public_form", "email", "sms", "phone", "error_alert"]);

export function agentFixTrust(
  ticket: { source?: string; isInternalFeedback?: boolean },
  submitterIsStaff: boolean,
): AgentFixTrust {
  if (ticket.source === "agent") return "staff";
  if (ticket.source === undefined || OUTSIDE_TEXT_SOURCES.has(ticket.source)) return "untrusted";
  return (ticket.isInternalFeedback ?? submitterIsStaff) ? "staff" : "untrusted";
}

const NEEDS_PERSON_PREFIX = "Agent fix needs a person:";
const PAUSED_PREFIX = "Agent fix paused:";

export function agentFixBlocker(kind: "needs_person" | "paused", reason: string): string {
  return `${kind === "needs_person" ? NEEDS_PERSON_PREFIX : PAUSED_PREFIX} ${reason}`.slice(0, AGENT_FIX_NOTE_MAX);
}

export function isAgentFixBlocker(reason: string | undefined): boolean {
  return reason !== undefined && (reason.startsWith(NEEDS_PERSON_PREFIX) || reason.startsWith(PAUSED_PREFIX));
}

export const AGENT_FIX_SAFETY_DEFAULTS = {
  enabled: true,
  maxPerDay: 40,
  maxPerTicketPerDay: 3,
  maxChainDepth: 3,
} as const;

export const AGENT_FIX_SAFETY_LIMITS = {
  maxPerDay: 500,
  maxPerTicketPerDay: 10,
  maxChainDepth: 10,
} as const;

export const AGENT_FIX_MAX_ATTEMPTS = 2;

export const AGENT_FIX_UNEXPLAINED_STOP = "the run stopped without saying why";

export const AGENT_FIX_MODEL_REFUSED_PREFIX = "The model call was refused before any work:";

export const AGENT_FIX_REFUSED_RETRY_MS = 6 * 60 * 60 * 1000;

export const AGENT_FIX_REFUSAL_WINDOW_MS = 15 * 60 * 1000;

export const AGENT_FIX_MAX_REFUSED_ATTEMPTS = 4;

export const AGENT_FIX_LATE_REFUSAL_PREFIX = "Reported as a refused model call, too long after the run started to be one:";

export function agentFixStopSummary(run: { status: string; requestedAt: number }, summary: string, now: number): string {
  if (!isAgentFixModelRefusal(summary)) return summary;
  if (run.status === "working" && now - run.requestedAt <= AGENT_FIX_REFUSAL_WINDOW_MS) return summary;
  return `${AGENT_FIX_LATE_REFUSAL_PREFIX}${summary.slice(AGENT_FIX_MODEL_REFUSED_PREFIX.length)}`;
}

export function isAgentFixModelRefusal(reason: string | undefined): boolean {
  return reason?.startsWith(AGENT_FIX_MODEL_REFUSED_PREFIX) === true;
}

export const AGENT_FIX_DISPATCH_EVENT = "agent-fix";

export const AGENT_FIX_NOTE_MAX = 2000;

export function canRequestAgentFix(role: string | undefined): boolean {
  return isAdminRole(role);
}

export const AGENT_FIX_STALE_MS = 3 * 60 * 60 * 1000;

export function agentFixInFlight(
  agentFix: { status: AgentFixStatus; statusAt: number } | undefined,
  now: number,
): boolean {
  if (!agentFix) return false;
  if (agentFix.status !== "queued" && agentFix.status !== "working") return false;
  return now - agentFix.statusAt < AGENT_FIX_STALE_MS;
}

export function agentFixBlocksRequest(
  ticket: {
    agentFix?: { status: AgentFixStatus; statusAt: number; pullRequestUrl?: string };
    githubLinks?: ReadonlyArray<{ url?: string; state?: string }>;
  },
  now: number,
): boolean {
  const agentFix = ticket.agentFix;
  if (agentFixInFlight(agentFix, now)) return true;
  if (agentFix?.status !== "pr_open") return false;
  const pullRequest = (ticket.githubLinks ?? []).find((link) => link.url === agentFix.pullRequestUrl);
  if (!pullRequest) return now - agentFix.statusAt < AGENT_FIX_STALE_MS;
  return pullRequest.state !== "closed" && pullRequest.state !== "merged";
}

export function agentFixRequestId(requestedAt: number): string {
  return String(requestedAt);
}
